import { ElectronBridge, Locales } from '@griply/types';
import {
  DateFormatOptions,
  TimeFormatOptions,
} from 'shared/types/date-format-options';
import { Goal, NewGoalWithID } from 'shared/types/goal';
import { InsightsTimeframes } from 'shared/types/insights';
import { LifeArea, LocalizedLifeArea } from 'shared/types/life-area';
import { Relationship } from 'shared/types/relationship';
import { NewTagWithID, Tag } from 'shared/types/tag';
import { CreateTask, NewSubTask } from 'shared/types/task';
import { WeekDays } from 'shared/types/week-days';

export type TaskCreationData = {
  goals: Goal[];
  lifeAreas: (LifeArea | LocalizedLifeArea)[];
  tags: Tag[];
  relationships: Relationship[];

  timeframe: InsightsTimeframes;
  weekStartsOn: WeekDays;
  timeFormat: TimeFormatOptions;
  dateFormat: DateFormatOptions;
  language?: Locales;
  createHabitAllowed: boolean;
  createHabitTargetAllowed: boolean;
  createHabitIconAllowed: boolean;
  createGoalAllowed: boolean;
};

export enum ElectronPushTokenErrorCode {
  PermissionDenied = 'permission-denied',
  RegistrationFailed = 'registration-failed',
}

export type ElectronPushTokenResult =
  | string
  | null
  | { token?: string; error?: ElectronPushTokenErrorCode };

export class PushRegistrationError extends Error {
  readonly code: ElectronPushTokenErrorCode;

  // rawCode keeps an unrecognized bridge error distinguishable from a genuine registration failure in Sentry
  constructor(code: ElectronPushTokenErrorCode, rawCode?: string) {
    super(
      rawCode && rawCode !== code
        ? `Push registration failed: ${code} (bridge: ${rawCode})`
        : `Push registration failed: ${code}`,
    );
    this.name = 'PushRegistrationError';
    this.code = code;
  }
}

export type TaskExtraData = {
  subTasks?: NewSubTask[];
  goals?: NewGoalWithID[];
  tags?: NewTagWithID[];
};

type ElectronPushTokenBridge = {
  // shells ≤3.4.x expose getFcmToken (raw FCM string); ≥3.50.0 expose getPushToken ({token}/{error} on mac, string on windows)
  getFcmToken?: () => Promise<string | null>;
  getPushToken?: () => Promise<ElectronPushTokenResult>;
};

// The shared bridge is the shell's current contract; web keeps the wider push-token surface for shells still in the wild.
export type Electron = Omit<
  ElectronBridge<TaskCreationData, CreateTask, TaskExtraData>,
  'getFcmToken' | 'getPushToken'
> &
  ElectronPushTokenBridge;

export type PopoverElectron = Electron['popover'];
